import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import { useOrgs, useVersions } from '@/lib/hooks'
import { DeprecateModal, UndeprecateModal } from '@/shared/components/ModuleModals'
import { Breadcrumbs, BreadcrumbLink, BreadcrumbSeparator } from '@/shared/components/Breadcrumb'
import { api } from '@/lib/api'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { useToast } from '@/context/ToastContext'
import { Tooltip } from '@/shared/components/Tooltip'
import { VersionDetailTabs } from '@/features/modules/VersionDetailTabs'
import { VersionRow } from '@/features/modules/components/VersionRow'

export function ModuleDetail() {
  const { orgSlug = '', name = '', provider = '', version: versionParam } = useParams()
  const navigate = useNavigate()
  const orgs     = useOrgs()
  const versions = useVersions(orgSlug, name)
  const orgRole  = (orgs.data?.orgs ?? []).find((o) => o.slug === orgSlug)?.role ?? ''
  const [selectedVersion, setSelectedVersion]     = useState<string | null>(versionParam ?? null)
  const { showToast }                             = useToast()
  const [deprecatingVersion, setDeprecatingVersion]     = useState<string | null>(null)
  const [undeprecatingVersion, setUndeprecatingVersion] = useState<string | null>(null)

  const versionList   = versions.data?.versions ?? []
  const activeVersion = selectedVersion ?? versionList[0]?.version ?? null
  const canAdmin      = ['owner', 'admin'].includes(orgRole)
  const canMember     = ['owner', 'admin', 'member'].includes(orgRole)
  const [rescanBusy, setRescanBusy] = useState<string | null>(null)

  // Surface version-list load errors as a toast, consistent with every other
  // action on this page (which already use showToast).
  useEffect(() => {
    if (versions.error) showToast(versions.error, 'error')
  }, [versions.error, showToast])

  async function handleRescan(version: string) {
    setRescanBusy(version)
    try {
      await api.post(`/v1/modules/${orgSlug}/${name}/_/${version}/rescan`, {})
      showToast(`Rescan queued for v${version}.`, 'success')
      versions.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setRescanBusy(null)
    }
  }

  return (
    <Layout title="// Modules">
      <Breadcrumbs>
        <BreadcrumbLink to={`/org/${orgSlug}/modules`}>Modules</BreadcrumbLink>
        <BreadcrumbSeparator />
        <span style={{ fontFamily: 'var(--mono)', color: 'var(--text0)' }}>{name}</span>
        <BreadcrumbSeparator />
        <span style={{ fontFamily: 'var(--mono)' }}>{provider}</span>
      </Breadcrumbs>

      {versions.loading && (
        <SkeletonTable
          headers={['Version', 'Source', 'Security', 'Consumable', 'Last scanned', 'Downloads', 'Last downloaded', ...((canMember || canAdmin) ? [''] : [])]}
          firstColTwoLines={false}
        />
      )}

      {versionList.length > 0 && (
        <div className="table-wrap fu" style={{ marginBottom: 24 }}>
          <table>
            <thead>
              <tr>
                <th>
                  <Tooltip text="Published module version. Click a row to open its detail below." placement="top">
                    <span tabIndex={0} className="cursor-help">Version</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="The repository, ref and commit this version was published from." placement="top">
                    <span tabIndex={0} className="cursor-help">Source</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Worst-severity findings from this version's scan, or the scan's state." placement="top">
                    <span tabIndex={0} className="cursor-help">Security</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Whether this version can be consumed, or is blocked by policy or by hand." placement="top">
                    <span tabIndex={0} className="cursor-help">Consumable</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="When this version's last scan completed." placement="top">
                    <span tabIndex={0} className="cursor-help">Last scanned</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Total downloads of this version through TerraMantle." placement="top">
                    <span tabIndex={0} className="cursor-help">Downloads</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="When this version was last downloaded." placement="top">
                    <span tabIndex={0} className="cursor-help">Last downloaded</span>
                  </Tooltip>
                </th>
                {(canMember || canAdmin) && <th></th>}
              </tr>
            </thead>
            <tbody>
              {versionList.map((v, i) => (
                <VersionRow
                  key={v.version}
                  v={v}
                  isLatest={i === 0}
                  active={activeVersion === v.version}
                  slug={orgSlug}
                  name={name}
                  provider={provider}
                  canMember={canMember}
                  canAdmin={canAdmin}
                  rescanBusy={rescanBusy === v.version}
                  onSelect={() => { setSelectedVersion(v.version); navigate(`/org/${orgSlug}/modules/${name}/${provider}/${v.version}`, { replace: true }) }}
                  onRescan={() => handleRescan(v.version)}
                  onUndeprecate={() => setUndeprecatingVersion(v.version)}
                  onDeprecate={() => setDeprecatingVersion(v.version)}
                  onChanged={() => versions.refetch()}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deprecatingVersion && (
        <DeprecateModal
          namespace={orgSlug}
          name={name}
          provider={provider}
          fixedVersion={deprecatingVersion}
          latestVersion={deprecatingVersion}
          onClose={() => setDeprecatingVersion(null)}
          onDeprecated={() => {
            const v = deprecatingVersion
            setDeprecatingVersion(null)
            showToast(`v${v} deprecated.`, 'success')
            versions.refetch()
          }}
        />
      )}

      {undeprecatingVersion && (
        <UndeprecateModal
          namespace={orgSlug}
          name={name}
          provider={provider}
          version={undeprecatingVersion}
          onClose={() => setUndeprecatingVersion(null)}
          onUndeprecated={() => {
            const v = undeprecatingVersion
            setUndeprecatingVersion(null)
            showToast(`v${v} undeprecated.`, 'success')
            versions.refetch()
          }}
        />
      )}

      {activeVersion && (
        <VersionDetailTabs
          slug={orgSlug}
          name={name}
          provider={provider}
          version={activeVersion}
          canOwner={orgRole === 'owner'}
        />
      )}

      {!versions.loading && versionList.length === 0 && (
        <EmptyState icon="▲" title="No versions published yet." />
      )}
    </Layout>
  )
}
